import { useMutation } from '@apollo/client';
import { User } from 'mission-control-common-components';
import { useCallback } from 'react';

import { UPDATE_TRAFFICKING_CUSTOM_FILTER } from '../mutations/updateTraffickingCustomFilter';
import type {
  TraffickingCustomFilterSummary,
  UpdateTraffickingCustomFilterInput,
  UpdateTraffickingCustomFilterMutationResponse,
  UpdateTraffickingCustomFilterMutationVariables,
} from '../types';

type UseUpdateCustomFilterResult = {
  updateFilter: (id: string, input: UpdateTraffickingCustomFilterInput) => Promise<TraffickingCustomFilterSummary>;
  loading: boolean;
  error: Error | undefined;
};

export function useUpdateCustomFilter(): UseUpdateCustomFilterResult {
  const [mutate, { loading, error }] = useMutation<
    UpdateTraffickingCustomFilterMutationResponse,
    UpdateTraffickingCustomFilterMutationVariables
  >(UPDATE_TRAFFICKING_CUSTOM_FILTER, {
    refetchQueries: ['GET_TRAFFICKING_CUSTOM_FILTERS'],
  });

  const updateFilter = useCallback(
    async (id: string, input: UpdateTraffickingCustomFilterInput): Promise<TraffickingCustomFilterSummary> => {
      const result = await mutate({ variables: { id, input } });
      if (!result.data) {
        throw new Error('Failed to update custom filter');
      }
      const raw = result.data.updateTraffickingCustomFilter;
      const currentUser = User.getUserInfo().userInfo?.email ?? '';
      return {
        id: raw.id,
        name: raw.filterName,
        visibility: raw.visibility as TraffickingCustomFilterSummary['visibility'],
        type: raw.filterType as TraffickingCustomFilterSummary['type'],
        createdByMe: (raw.createdBy ?? '').toLowerCase() === currentUser.toLowerCase(),
      };
    },
    [mutate]
  );

  return { updateFilter, loading, error };
}
